import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";

import { REQUEST_STATUS, REQUEST_STATUS_LABEL } from "../constants/status";
import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import { InputCalendar, InputSelect } from "components/v2";
import { Toast } from "../../../../../components/toast";
import ToolBarThreeColumns from "../../../../../components/Table/ToolBarThreeColumns";

import { debounce } from "../../../../../utils/debounce";
import { GeneralNames } from "../../../../../namesConstants/names";

import { StoreContext } from "../../../../../business/Provider";

import { usePeriodTypes } from "shared/hooks";
import { toUiModel } from "shared/hooks/usePeriodTypes";

import type { PeriodTypeUiT, PeriodUiT } from "shared/domain/ui/periodType.model";
import type { AccessRoleState } from "../utils/permissions";

import { canApplyRequests } from "../utils/permissions";

type TableFiltersT = {
  status: string[];
  startDate: Date | null;
  endDate: Date | null;
  periodTypeId: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
};

type DefaultPeriodSelectionT = {
  type: string | null;
  period: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
};

const getDefaultPeriodSelection = (
  periodTypes?: PeriodTypeUiT[],
): DefaultPeriodSelectionT => {
  if (!periodTypes || periodTypes.length === 0)
    return {
      type: null,
      period: null,
      periodStartDate: null,
      periodEndDate: null,
    };

  const defaultType = periodTypes[0];
  const today = moment();

  const currentPeriod: PeriodUiT | null =
    defaultType.periods.find(
      (p) =>
        p.initialDate &&
        p.finalDate &&
        today.isSameOrAfter(moment(p.initialDate, "YYYY-MM-DD"), "day") &&
        today.isSameOrBefore(moment(p.finalDate, "YYYY-MM-DD"), "day"),
    ) ??
    defaultType.periods[0] ??
    null;

  return {
    type: defaultType.id,
    period: currentPeriod?.id ?? null,
    periodStartDate: currentPeriod?.initialDate ?? null,
    periodEndDate: currentPeriod?.finalDate ?? null,
  };
};

type TableFormPropsT = {
  tableFilters: TableFiltersT;
  updateTableFilters: (partial: Partial<TableFiltersT>) => void;
  setSearchTextFilter: (updater: (prev: any) => any) => void;
  resetSearchTextFilter: () => void;
  onClearSelectedRows: () => void;
  onExportToExcel: () => void | Promise<void>;
  onBulkApprove: () => void;
  onBulkReject: () => void;
  onBulkApply: () => void;
};

export const TableForm = ({
  tableFilters,
  updateTableFilters,
  setSearchTextFilter,
  resetSearchTextFilter,
  onClearSelectedRows,
  onExportToExcel,
  onBulkApprove,
  onBulkReject,
  onBulkApply,
}: TableFormPropsT) => {
  const {
    data: periodTypes,
    isLoading,
    importPeriodTypes: { mutateAsync: importPeriods, isPending: isImporting },
  } = usePeriodTypes();

  const [selectedPeriodValue, setSelectedPeriodValue] = useState<{
    period: string | null;
  }>({
    period: null,
  });

  const hasInitialized = useRef(false);

  const applyDefaultPeriod = useCallback(
    (defaults: DefaultPeriodSelectionT) => {
      setSelectedPeriodValue({ period: defaults.period });

      updateTableFilters({
        periodTypeId: defaults.type,
        periodStartDate: defaults.periodStartDate,
        periodEndDate: defaults.periodEndDate,
      });
    },
    [updateTableFilters],
  );

  useEffect(() => {
    if (hasInitialized.current || !periodTypes || periodTypes.length === 0)
      return;

    hasInitialized.current = true;

    const defaults = getDefaultPeriodSelection(periodTypes);

    if (defaults.type) {
      importPeriods({ periodTypeId: defaults.type })
        .then((result) => {
          const uiData = toUiModel(result);

          applyDefaultPeriod(getDefaultPeriodSelection(uiData));
        })
        .catch((error) => {
          applyDefaultPeriod(defaults);

          Toast(
            "warning",
            VACATION_REQUESTS_STRINGS.IMPORT_PERIOD_ERROR_TITLE,
            error?.message,
          );
        });
    }
  }, [periodTypes, applyDefaultPeriod, importPeriods]);

  const [searchText, setSearchText] = useState("");

  const { getAccessRoleState } = useContext(StoreContext);
  const canApply = canApplyRequests(getAccessRoleState as AccessRoleState);

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTextFilter((prevFilters: any) => ({
          ...prevFilters,
          global: {
            ...prevFilters.global,
            value,
          },
        }));
      }, 500),
    [setSearchTextFilter],
  );

  const memoizedOptions = useMemo(
    () => [
      { label: GeneralNames.GeneralAll, value: "all" },
      ...Object.values(REQUEST_STATUS).map((status) => ({
        label: REQUEST_STATUS_LABEL[status],
        value: status,
      })),
    ],
    [],
  );

  const memoizedSelectedPeriods = useMemo(() => {
    if (!tableFilters.periodTypeId) return [];

    return (
      periodTypes?.find((pt) => pt.id === tableFilters.periodTypeId)
        ?.periods || []
    );
  }, [tableFilters.periodTypeId, periodTypes]);

  const onUpdateSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedUpdate(value);
  };

  const onChangeSelectedStatus = (e: { target: { value: string[] } }) => {
    onClearSelectedRows();
    updateTableFilters({ status: e.target.value });
  };

  const onChangePeriodType = async (e: { target: { value: string } }) => {
    const newTypeId = e.target.value;
    const selectedType = periodTypes?.find((pt) => pt.id === newTypeId);
    const today = moment();

    const currentPeriod: PeriodUiT | null =
      selectedType?.periods.find(
        (p) =>
          p.initialDate &&
          p.finalDate &&
          today.isSameOrAfter(moment(p.initialDate, "YYYY-MM-DD"), "day") &&
          today.isSameOrBefore(moment(p.finalDate, "YYYY-MM-DD"), "day"),
      ) ??
      selectedType?.periods[0] ??
      null;

    setSelectedPeriodValue({ period: currentPeriod?.id ?? null });
    updateTableFilters({
      periodTypeId: newTypeId,
      periodStartDate: currentPeriod?.initialDate ?? null,
      periodEndDate: currentPeriod?.finalDate ?? null,
    });

    try {
      await importPeriods({ periodTypeId: newTypeId });
    } catch (error) {
      Toast(
        "warning",
        VACATION_REQUESTS_STRINGS.IMPORT_PERIOD_ERROR_TITLE,
        error?.message,
      );
    }
  };

  const onChangePeriod = (e: { target: { value: string } }) => {
    const newPeriodId = e.target.value;
    const selectedPeriod =
      memoizedSelectedPeriods.find((p) => p.id === newPeriodId) ?? null;

    setSelectedPeriodValue((prev) => ({ ...prev, period: newPeriodId }));
    updateTableFilters({
      periodStartDate: selectedPeriod?.initialDate ?? null,
      periodEndDate: selectedPeriod?.finalDate ?? null,
    });
  };

  const onChangeStartDate = (value: Date | null) => {
    updateTableFilters({ startDate: value });
  };

  const onChangeEndDate = (value: Date | null) => {
    updateTableFilters({ endDate: value });
  };

  const onClearSearchText = () => {
    setSearchText("");
    resetSearchTextFilter();
  };

  const onCleanFilters = () => {
    onClearSearchText();

    const allValues = memoizedOptions
      .filter((o) => o.value !== "all")
      .map((o) => o.value);

    const defaults = getDefaultPeriodSelection(periodTypes);

    updateTableFilters({
      status: allValues,
      startDate: null,
      endDate: null,
      periodTypeId: defaults.type,
      periodStartDate: defaults.periodStartDate,
      periodEndDate: defaults.periodEndDate,
    });

    setSelectedPeriodValue({ period: defaults.period });
  };

  const iconsData = [
    {
      icon: "reject",
      title: "Rechazar",
      onClick: onBulkReject,
    },
    { icon: "accept", title: "Aprobar solicitud", onClick: onBulkApprove },
    ...(canApply
      ? [
          {
            icon: "apply",
            title: VACATION_REQUESTS_STRINGS.APPLY_BUTTON_TOOLTIP,
            onClick: onBulkApply,
          },
        ]
      : []),
    { icon: "clear", title: "Limpiar Filtros", onClick: onCleanFilters },
    {
      icon: "excel",
      title: GeneralNames.GeneralExportToExcel,
      onClick: onExportToExcel,
    },
  ];

  return (
    <div
      role="search"
      aria-label="Filtros de solicitudes de vacaciones"
      className="flex flex-col gap-2"
    >
      <ToolBarThreeColumns
        value={searchText}
        clearTextLabel={onClearSearchText}
        onChange={onUpdateSearchText}
        placeholder="Buscar"
        filterOpc={memoizedOptions}
        selectFilter={tableFilters} // Status is selected automatically
        handleFilterChange={onChangeSelectedStatus}
        iconsDataPanel={iconsData}
        isMultiSelect
      />
      <fieldset
        className="grid grid-cols-2 gap-1 w-full xl:flex xl:items-end xl:w-4/5 xl:pr-[.25rem] 2xl:flex 2xl:items-end 2xl:w-4/5 2xl:pr-[.25rem]"
        aria-label="Filtros por tipos de periodo"
      >
        <div className="min-w-0  xl:w-1/4 2xl:w-1/4">
          <InputSelect
            id="periodType"
            label="Tipo de periodo"
            options={periodTypes || []}
            optionLabel="name"
            optionValue="id"
            onChange={onChangePeriodType}
            value={tableFilters.periodTypeId}
            isLoading={isLoading || isImporting}
          />
        </div>
        <div className="min-w-0  xl:w-1/4 2xl:w-1/4">
          <InputSelect
            id="period"
            label="Periodo"
            options={memoizedSelectedPeriods}
            optionLabel="label"
            optionValue="id"
            onChange={onChangePeriod}
            value={selectedPeriodValue.period}
          />
        </div>
        <div className="min-w-0  xl:w-1/4 2xl:w-1/4">
          <InputCalendar
            id="startDate"
            name="startDate"
            value={tableFilters.startDate}
            onChange={(e) => onChangeStartDate(e.target.value)}
            placeholder="Fecha de Inicio"
          />
        </div>
        <div className="min-w-0  xl:w-1/4 2xl:w-1/4">
          <InputCalendar
            id="endDate"
            name="endDate"
            value={tableFilters.endDate}
            onChange={(e) => onChangeEndDate(e.target.value)}
            placeholder="Fecha de Fin"
          />
        </div>
      </fieldset>
    </div>
  );
};
